import { useState, useEffect } from "react";
import {
  MantineProvider,
  Center,
  Box,
  Text,
  Button,
  Group,
  Badge,
  Loader,
} from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { IconRefresh, IconWifiOff, IconCloudOff } from "@tabler/icons-react";
import { RestaurantMenu } from "./components/RestaurantMenu";
import { MenuSkeleton } from "./components/SkeletonLoader";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { fetchMenu } from "./services/api";
import { useSeo } from "./hooks/useSeo";
import type { RestaurantMenuData } from "./types";
import "@mantine/core/styles.css";
import "@mantine/carousel/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/global.css";

function App() {
  const [menuData, setMenuData] = useState<RestaurantMenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [usingCachedData, setUsingCachedData] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const restaurantId = (import.meta as any).env.VITE_RESTAURANT_ID;

  // Build SEO values from API response when available
  const categoryNames = menuData?.menus?.categories.map((c) => c.name) ?? [];
  const seoTitle = menuData
    ? `${menuData.name} — Menu | ${menuData.location}`
    : "Restaurant Menu";
  const seoDescription = menuData
    ? `Explore the menu at ${menuData.name} in ${menuData.location}. Contact: ${menuData.contactNo}. Categories: ${categoryNames.join(", ")}`
    : undefined;
  const rawImage = menuData?.logo ?? null;
  const seoImage = rawImage
    ? rawImage.startsWith("http")
      ? rawImage
      : `${window.location.origin}${rawImage}`
    : `${window.location.origin}/logo.png`;
  const seoKeywords = Array.from(
    new Set(
      [menuData?.name, menuData?.location, ...categoryNames].filter(Boolean),
    ),
  ) as string[];

  const structuredData = menuData
    ? {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        name: menuData.name,
        image: seoImage,
        telephone: menuData.contactNo,
        address: {
          "@type": "PostalAddress",
          streetAddress: menuData.location,
        },
        hasMenu: {
          "@type": "Menu",
          name: `${menuData.name} Menu`,
          hasMenuSection: menuData.menus.categories.map((cat) => ({
            "@type": "MenuSection",
            name: cat.name,
            hasMenuItem: cat.items.map((it) => ({
              "@type": "MenuItem",
              name: it.name,
              description: it.description,
              offers:
                it.price != null
                  ? { "@type": "Offer", price: it.price }
                  : undefined,
            })),
          })),
        },
      }
    : undefined;

  useSeo({
    title: seoTitle,
    description: seoDescription,
    image: seoImage,
    url: window.location.href,
    keywords: seoKeywords,
    structuredData,
  });

  const loadMenu = async (skipCache = false) => {
    if (!restaurantId) {
      setError("Restaurant ID not configured");
      setLoading(false);
      setInitialLoad(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetchMenu(restaurantId, skipCache);

      if (response.success && response.data) {
        const filteredData = {
          ...response.data,
          menus: {
            categories: response.data.menus.categories
              .map((category) => ({
                ...category,
                items: category.items.filter(
                  (item) => item.status === "AVAILABLE",
                ),
              }))
              .filter((category) => category.items.length > 0),
          },
        };
        setMenuData(filteredData);
        setUsingCachedData(
          !!response.error?.includes("cached") ||
            (!navigator.onLine && !!response.data),
        );
        setError(null);
      } else {
        setError(response.error || "Failed to load menu");
      }
    } catch (err) {
      console.error("Error loading menu:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleRefresh = async () => {
    if (!isOnline) {
      setError("Cannot refresh while offline. Please check your connection.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    await loadMenu(true);
  };

  useEffect(() => {
    loadMenu();

    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [restaurantId]);

  if (loading && initialLoad) {
    return (
      <MantineProvider defaultColorScheme="light">
        <MenuSkeleton itemsCount={12} />
      </MantineProvider>
    );
  }

  if (error && !menuData) {
    return (
      <MantineProvider defaultColorScheme="light">
        <Center style={{ height: "100vh" }}>
          <Box ta="center" p="xl">
            <Text size="xl" c="red" mb="md" fw={700}>
              {!isOnline ? "📡 You are offline" : "❌ Error Loading Menu"}
            </Text>
            <Text c="dimmed" mb="lg" maw={400}>
              {error}
            </Text>
            {!isOnline && (
              <Button
                onClick={() => window.location.reload()}
                variant="filled"
                color="blue"
                size="lg"
              >
                Try Again
              </Button>
            )}
            {isOnline && (
              <Button
                onClick={handleRefresh}
                variant="filled"
                color="orange"
                size="lg"
                leftSection={<IconRefresh size={18} />}
              >
                Refresh Menu
              </Button>
            )}
          </Box>
        </Center>
      </MantineProvider>
    );
  }

  return (
    <MantineProvider defaultColorScheme="light">
      <Notifications />
      <OfflineIndicator />

      {usingCachedData && !isOnline && menuData && (
        <Box
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            backgroundColor: "#fff3cd",
            borderBottom: "1px solid #ffeeba",
            padding: "10px 20px",
          }}
        >
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="xs">
              <IconWifiOff size={18} color="#856404" />
              <Text size="sm" c="#856404">
                ⚡ You are viewing cached data.{" "}
                {!isOnline && "You are offline."}
              </Text>
              <Badge size="sm" variant="light" color="yellow">
                Offline Mode
              </Badge>
            </Group>
            {!isOnline && (
              <Button size="xs" variant="subtle" color="yellow" disabled>
                Waiting for connection...
              </Button>
            )}
            {isOnline && (
              <Button
                size="xs"
                variant="light"
                color="orange"
                onClick={handleRefresh}
                leftSection={<IconRefresh size={14} />}
              >
                Refresh to Update
              </Button>
            )}
          </Group>
        </Box>
      )}

      {menuData && <RestaurantMenu menuData={menuData} />}

      {loading && !initialLoad && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader size="xl" variant="dots" color="orange" />
        </Box>
      )}

       <footer className="siteFooter" aria-hidden>
          <div className="siteFooterInner">
            <div className="siteFooterRight">
              <a
                href="https://xpoundai.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                © {new Date().getFullYear()}{" "}
                <span className="brandName">Xpound AI Solution Pvt. Ltd.</span>{" "}
                All Rights Reserved
              </a>
            </div>
          </div>
        </footer>
    </MantineProvider>
  );
}

export default App;
